// Components/Services/ADUAndCustomHomes/Overview.jsx
import React from "react";
import { motion } from "framer-motion";
import { FiHome, FiTrendingUp, FiCheckCircle, FiLayers, FiTool, FiShield } from "react-icons/fi";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const fadeRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const aduTypes = [
  {
    icon: <FiHome className="w-5 h-5 text-[#212529]" />,
    title: "Detached ADU",
    desc: "A fully separate unit on your property — maximum privacy and rental income potential.",
  },
  {
    icon: <FiCheckCircle className="w-5 h-5 text-[#212529]" />,
    title: "Attached ADU",
    desc: "Built onto your existing home — cost-effective and ideal for multi-generational living.",
  },
  {
    icon: <FiTrendingUp className="w-5 h-5 text-[#212529]" />,
    title: "Garage Conversion",
    desc: "Transform unused garage space into a permitted, livable unit with full amenities.",
  },
];

const customHomeFeatures = [
  {
    icon: <FiLayers className="w-5 h-5 text-[#212529]" />,
    title: "Fully Custom Design",
    desc: "Every floor plan, elevation, and finish is tailored to your lifestyle and vision — nothing off the shelf.",
  },
  {
    icon: <FiTool className="w-5 h-5 text-[#212529]" />,
    title: "Ground-Up Construction",
    desc: "We manage every phase from site prep and foundation to framing, systems, and final finishes.",
  },
  {
    icon: <FiShield className="w-5 h-5 text-[#212529]" />,
    title: "Third-Generation Craftsmanship",
    desc: "Built to last — with the same standards our family has applied for over 45 years in the trades.",
  },
];

const aduStats = [
  { number: "3",      label: "ADU Types We Build"      },
  { number: "$120K+", label: "Starting Investment"      },
  { number: "100%",   label: "Permits Handled In-House" },
  { number: "A–Z",    label: "Project Management"       },
];

const customStats = [
  { number: "15+",    label: "Years of Experience"    },
  { number: "100%",   label: "Custom Floor Plans"     },
  { number: "$300K+", label: "Starting Investment"    },
  { number: "3rd",    label: "Generation Contractors" },
];

const Overview = () => {
  return (
    <section className="bg-white">

      {/* ── ADU SECTION ── */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Left — image */}
          <motion.div
            variants={fadeLeft}
            className="relative rounded-2xl overflow-hidden shadow-xl min-h-[380px] group"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <img
              src="/Images/ADUs.png"
              alt="ADU construction by Ashlar Building Group"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5">
              <span className="bg-white/90 backdrop-blur-sm text-[#212529] text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                Permitted ADU Build
              </span>
            </div>
          </motion.div>

          {/* Right — ADU text */}
          <motion.div variants={fadeRight} className="flex flex-col justify-center">
            <p className="text-[#6c757d] uppercase tracking-widest text-xs font-bold mb-2">
              Accessory Dwelling Units
            </p>
            <h3 className="text-2xl md:text-3xl font-black text-[#212529] leading-tight mb-3">
              Add Space. Add Value.<br />Add Income.
            </h3>
            <p className="text-[#495057] text-sm leading-relaxed mb-3">
              An ADU is a secondary living space built on your existing property. California's ADU laws make it easier than ever to add a permitted unit — whether it's a detached backyard cottage, an attached suite, or a converted garage. We handle the entire process from feasibility assessment and city permits to full construction and final inspection.
            </p>
            <p className="text-[#495057] text-sm leading-relaxed mb-5">
              ADUs are one of the smartest investments a California property owner can make. They increase your property value, generate ongoing rental income, and provide flexible space for family members or guests — all without relocating.
            </p>
            <div className="space-y-3">
              {aduTypes.map((type, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  whileHover={{ x: 6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="flex items-start gap-4 bg-[#f8f9fa] rounded-xl px-4 py-3 border border-[#dee2e6] cursor-default"
                >
                  <div className="w-9 h-9 rounded-full bg-white border border-[#dee2e6] flex items-center justify-center flex-shrink-0 shadow-sm">
                    {type.icon}
                  </div>
                  <div>
                    <p className="text-[#212529] font-bold text-sm">{type.title}</p>
                    <p className="text-[#6c757d] text-xs mt-0.5 leading-relaxed">{type.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* ── ADU STATS BAR ── */}
      <div className="pb-12 px-4 md:px-6">
        <motion.div
          className="max-w-6xl mx-auto"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div
            variants={fadeUp}
            className="bg-[#343a40] rounded-2xl shadow-lg px-8 py-8 md:px-12 md:py-10"
          >
            <div className="flex flex-col lg:flex-row items-center gap-6">
              <div className="lg:w-[26%] text-center flex flex-col items-center flex-shrink-0">
                <p className="text-[#dee2e6] text-base font-medium whitespace-nowrap">Maximize your property with</p>
                <p className="text-xl md:text-2xl font-bold text-[#f8f9fa] whitespace-nowrap">Smart ADU Builds</p>
              </div>
              <div className="hidden lg:block w-px h-14 bg-[#495057] flex-shrink-0" />
              <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                {aduStats.map((stat, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    whileHover={{ scale: 1.1, y: -4 }}
                    transition={{ type: "spring", stiffness: 300, damping: 18 }}
                    className="flex flex-col items-center text-center cursor-default"
                  >
                    <span className="text-xl md:text-2xl font-bold text-[#f8f9fa] mb-1">{stat.number}</span>
                    <p className="text-xs md:text-sm text-[#dee2e6] font-medium whitespace-nowrap">{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* ── DIVIDER ── */}
      <div className="max-w-6xl mx-auto px-6">
        <div className="border-t border-[#dee2e6]" />
      </div>

      {/* ── CUSTOM HOMES SECTION ── */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Left — text */}
          <motion.div variants={fadeLeft} className="flex flex-col justify-center order-2 lg:order-1">
            <p className="text-[#6c757d] uppercase tracking-widest text-xs font-bold mb-2">
             New Custom Homes
            </p>
           <h3 className="text-2xl md:text-3xl font-black text-[#212529] leading-tight mb-3">
  Custom Homes Designed for You.<br />Built to Last for Generations.
</h3>
          <p className="text-[#495057] text-sm leading-relaxed mb-3">
  Building a new custom home is about creating a space that is completely your own. We work closely with homeowners, architects, and designers to turn ideas into a fully realized home tailored to your lifestyle, taste, and long-term needs. Every detail — from layout and materials to finishes and functionality — is thoughtfully planned to ensure your home reflects your vision.
</p>

<p className="text-[#495057] text-sm leading-relaxed mb-5">
  Our custom home process is structured and transparent. It begins with site evaluation and design collaboration, followed by permitting, construction planning, and phased building with consistent communication throughout the project. The result is a high-quality custom home built with precision, craftsmanship, and a standard meant to last for generations.
</p>
            <div className="space-y-3">
              {customHomeFeatures.map((feature, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  whileHover={{ x: 6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="flex items-start gap-4 bg-[#f8f9fa] rounded-xl px-4 py-3 border border-[#dee2e6] cursor-default"
                >
                  <div className="w-9 h-9 rounded-full bg-white border border-[#dee2e6] flex items-center justify-center flex-shrink-0 shadow-sm">
                    {feature.icon}
                  </div>
                  <div>
                    <p className="text-[#212529] font-bold text-sm">{feature.title}</p>
                    <p className="text-[#6c757d] text-xs mt-0.5 leading-relaxed">{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right — image */}
          <motion.div
            variants={fadeRight}
            className="relative rounded-2xl overflow-hidden shadow-xl min-h-[380px] group order-1 lg:order-2"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <img
              src="/Images/Behind-the-Scenes/BTS-5.jpeg"
              alt="Ground-up custom home by Ashlar Building Group"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5">
              <span className="bg-white/90 backdrop-blur-sm text-[#212529] text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                Custom Ground-Up Build
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* ── CUSTOM HOMES STATS BAR ── */}
      <div className="pb-14 px-4 md:px-6">
        <motion.div
          className="max-w-6xl mx-auto"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div
            variants={fadeUp}
            className="bg-[#212529] rounded-2xl shadow-lg px-8 py-8 md:px-12 md:py-10"
          >
            <div className="flex flex-col lg:flex-row items-center gap-6">
              <div className="lg:w-[26%] text-center flex flex-col items-center flex-shrink-0">
                <p className="text-[#dee2e6] text-base font-medium whitespace-nowrap">Your home, built with</p>
                <p className="text-xl md:text-2xl font-bold text-[#f8f9fa] whitespace-nowrap">Generational Craftsmanship</p>
              </div>
              <div className="hidden lg:block w-px h-14 bg-[#495057] flex-shrink-0" />
              <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                {customStats.map((stat, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    whileHover={{ scale: 1.1, y: -4 }}
                    transition={{ type: "spring", stiffness: 300, damping: 18 }}
                    className="flex flex-col items-center text-center cursor-default"
                  >
                    <span className="text-xl md:text-2xl font-bold text-[#f8f9fa] mb-1">{stat.number}</span>
                    <p className="text-xs md:text-sm text-[#dee2e6] font-medium whitespace-nowrap">{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

    </section>
  );
};

export default Overview;